/*
 * <collection-tab-slider> -- tabbed group switcher + prev/next arrows for the
 * homepage collection slider (sections/homepage-collection-slider.liquid).
 *
 * Each group is its own panel containing a <scroll-rail> (assets/scroll-rail.js).
 * Switching tabs just toggles [hidden] on the panels -- the revealed track goes
 * from 0 width to its real width, so scroll-rail's ResizeObserver re-measures the
 * line indicator on its own. The arrows scroll the active track by one page and
 * disable at the ends. Tabs/arrows are revealed only after .is-ready is added, so
 * with JS off the first group still renders and scrolls natively.
 */
if (!customElements.get('collection-tab-slider')) {
  customElements.define(
    'collection-tab-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('.hp-coll__tab'));
        this.panels = Array.from(this.querySelectorAll('.hp-coll__group'));
        this.prevBtn = this.querySelector('.hp-coll__nav-btn[data-dir="prev"]');
        this.nextBtn = this.querySelector('.hp-coll__nav-btn[data-dir="next"]');
        if (!this.panels.length) return;

        this.update = this.update.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab.dataset.group, true));
          tab.addEventListener('keydown', (event) => this.onKey(event));
        });
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));

        this.panels.forEach((panel) => {
          const track = panel.querySelector('[data-scroll-rail-track]');
          if (track) track.addEventListener('scroll', this.update, { passive: true });
        });
        window.addEventListener('resize', this.update, { passive: true });

        // Adopt the server-marked active panel (falls back to the first).
        const active = this.panels.find((panel) => !panel.hidden) || this.panels[0];
        this.select(active.dataset.group, false);
        this.classList.add('is-ready');
      }

      activeTrack() {
        const panel = this.panels.find((p) => !p.hidden);
        return panel ? panel.querySelector('[data-scroll-rail-track]') : null;
      }

      select(group, focus) {
        this.tabs.forEach((tab) => {
          const on = tab.dataset.group === group;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          if (on && focus) tab.focus();
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.group !== group;
        });
        this.update();
      }

      // Roving-tabindex keyboard support across the tablist.
      onKey(event) {
        const i = this.tabs.indexOf(event.target);
        if (i < 0) return;
        let next = -1;
        if (event.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
        if (next < 0) return;
        event.preventDefault();
        this.select(this.tabs[next].dataset.group, true);
      }

      page(direction) {
        const track = this.activeTrack();
        if (!track) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({ left: direction * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      }

      update() {
        const track = this.activeTrack();
        if (!track || !this.prevBtn || !this.nextBtn) return;
        const max = track.scrollWidth - track.clientWidth;
        const overflows = max > 1;
        this.prevBtn.disabled = !overflows || track.scrollLeft <= 1;
        this.nextBtn.disabled = !overflows || track.scrollLeft >= max - 1;
      }
    }
  );
}
